import React from 'react';
import { motion } from 'framer-motion';

const WorkWithPurposeSection = () => {
    const fadeUpVariant = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 }
        }
    };

    return (
        <section className="w-full py-[40px] md:py-[100px] bg-white font-sans">
            <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={fadeUpVariant}
                    className="mb-[32px] md:mb-[60px] max-w-[609px]"
                >
                    <h2 className="text-[20px] md:text-[30px] font-sora font-[600] text-[#01386A] tracking-tight mb-[12px] md:mb-[16px]">
                        Work with purpose
                    </h2>
                    <p className="text-[#49577A] text-[14px] md:text-[16px] font-[500] font-graphik leading-[1.6]">
                        At Mobikode, you’ll be part of a team that truly cares, working together to create real, positive change for our people, our clients, and the communities we’re proud to be part of.
                    </p>
                </motion.div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                    className="grid grid-cols-1 md:grid-cols-2 gap-[16px] md:gap-[24px]"
                >
                    <motion.div
                        variants={fadeUpVariant}
                        className="group relative w-full h-[300px] sm:h-[350px] md:h-[400px] overflow-hidden cursor-pointer"
                    >
                        <img
                            src="/assets/products/img2.png"
                            alt="Sustainability Abstract Light"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent"></div>

                        <div className="absolute bottom-0 left-0 p-[20px] sm:p-[24px] md:p-[32px] w-full flex flex-col justify-end">
                            <h3 className="text-white text-[16px] md:text-[20px] font-sora font-[600]">
                                Sustainability
                            </h3>
                            <div className="grid grid-rows-[1fr] md:grid-rows-[0fr] transition-[grid-template-rows] duration-500 ease-in-out group-hover:md:grid-rows-[1fr]">
                                <div className="overflow-hidden">
                                    <p className="text-white/90 text-[14px] md:text-[16px] font-graphik leading-[1.5] md:leading-[1.6] font-medium max-w-[470px] pt-[8px] md:pt-[12px] opacity-100 md:opacity-0 transition-opacity duration-500 delay-100 group-hover:md:opacity-100">
                                        At Mobikode, we build with purpose. Sustainability guides everything we create, from digital products to client solutions. We aim to make technology meaningful, reduce impact, and support a better future where innovation serves people, communities, and the planet together responsibly.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        variants={fadeUpVariant}
                        className="group relative w-full h-[300px] sm:h-[350px] md:h-[400px] overflow-hidden cursor-pointer"
                    >
                        <img
                            src="/assets/products/img3.png"
                            alt="Inclusion and Diversity Abstract Bokeh"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent"></div>

                        <div className="absolute bottom-0 left-0 p-[20px] sm:p-[24px] md:p-[32px] w-full flex flex-col justify-end">
                            <h3 className="text-white text-[16px] md:text-[20px] font-sora font-[600]">
                                Inclusion & Diversity
                            </h3>
                            <div className="grid grid-rows-[1fr] md:grid-rows-[0fr] transition-[grid-template-rows] duration-500 ease-in-out group-hover:md:grid-rows-[1fr]">
                                <div className="overflow-hidden">
                                    <p className="text-white/90 text-[14px] md:text-[16px] font-graphik leading-[1.5] md:leading-[1.6] font-medium max-w-[470px] pt-[8px] md:pt-[12px] opacity-100 md:opacity-0 transition-opacity duration-500 delay-100 group-hover:md:opacity-100">
                                        At Mobikode, we believe great work happens when everyone feels included and valued. We welcome different backgrounds, ideas, and perspectives, and we grow together by supporting each other. Inclusion and diversity guide how we build, collaborate, and innovate every day.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        variants={fadeUpVariant}
                        className="group relative w-full h-[250px] sm:h-[280px] md:h-[300px] md:col-span-2 overflow-hidden cursor-pointer"
                    >
                        <img
                            src="/assets/products/img4.png"
                            alt="Responsible AI Abstract Blocks"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent"></div>

                        <div className="absolute bottom-0 left-0 p-[20px] sm:p-[24px] md:p-[32px] w-full flex flex-col justify-end">
                            <h3 className="text-white text-[16px] md:text-[20px] font-sora font-[600]">
                                Responsible AI
                            </h3>
                            <div className="grid grid-rows-[1fr] md:grid-rows-[0fr] transition-[grid-template-rows] duration-500 ease-in-out group-hover:md:grid-rows-[1fr]">
                                <div className="overflow-hidden">
                                    <p className="text-white/90 text-[14px] md:text-[16px] font-graphik leading-[1.5] md:leading-[1.6] font-medium max-w-[800px] pt-[8px] md:pt-[12px] opacity-100 md:opacity-0 transition-opacity duration-500 delay-100 group-hover:md:opacity-100">
                                        At Mobikode, we build responsible AI with purpose, creating technology that is ethical, transparent, and human-centered, empowering people and businesses to grow while making a positive impact on society together.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                </motion.div>

            </div>
        </section>
    );
};

export default WorkWithPurposeSection;